import React, { useState, Fragment } from 'react'
import { AlertTriangle, Check, FileText, Link as LinkIcon, Lock, MessageSquare, X, Zap } from 'lucide-react'
import { Link } from 'react-router-dom'
import { DJENPublication, PublicationPriority } from '@/types/publication'
import { PublicationService } from '@/services/publicationService'
import { CopyableProcessNumber } from '@/components/ui/CopyableProcessNumber'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { PublicationActionModal } from './PublicationActionModal'
import { PublicationNotes } from './PublicationNotes'
import { SetPriorityModal } from './SetPriorityModal'
import { useNotifications } from '@/hooks/useNotifications'
import { cn } from '@/utils/cn'

import { getErrorMessage } from '../../utils/error.utils'
/** Tom do selo do score automático: quanto maior, mais destaque. */
function relevanceScoreClass(score: number): string {
  if (score >= 85) return 'bg-destructive/10 text-destructive'
  if (score >= 65) return 'border-border bg-background font-semibold text-foreground'
  if (score >= 45) return 'bg-secondary text-secondary-foreground'
  return 'bg-muted text-muted-foreground'
}

interface PublicationTableProps {
  publications: DJENPublication[]
  onView: (publication: DJENPublication) => void
  onUpdate: () => void
}

export function PublicationTable({ publications, onView, onUpdate }: PublicationTableProps) {
  const [loading, setLoading] = useState<Record<number, boolean>>({})
  const { showSuccess, showError } = useNotifications()
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    action: 'relevant' | 'irrelevant' | null
    publicationId: number | null
  }>({
    isOpen: false,
    action: null,
    publicationId: null
  })
  
  const [showNotes, setShowNotes] = useState<Record<number, boolean>>({})
  const [priorityModalState, setPriorityModalState] = useState<{
    isOpen: boolean
    publication: DJENPublication | null
  }>({
    isOpen: false,
    publication: null
  })

  const handleAction = async (id: number, action: () => Promise<unknown>, successMessage?: string) => {
    try {
      setLoading(prev => ({ ...prev, [id]: true }))
      await action()
      onUpdate()
      if (successMessage) {
        showSuccess('Ação realizada com sucesso', successMessage)
      }
    } catch (error) {
      console.error('Erro ao executar ação:', error)
      showError('Erro ao executar ação', error instanceof Error ? getErrorMessage(error) : 'Erro desconhecido')
    } finally {
      setLoading(prev => ({ ...prev, [id]: false }))
    }
  }

  const handleMarkRelevant = (publication: DJENPublication) => {
    setModalState({
      isOpen: true,
      action: 'relevant',
      publicationId: publication.id
    })
  }

  const handleMarkIrrelevant = (publication: DJENPublication) => {
    setModalState({
      isOpen: true,
      action: 'irrelevant',
      publicationId: publication.id
    })
  }

  const handleModalConfirm = async (reason: string) => {
    if (!modalState.publicationId || !modalState.action) return

    const actionText = modalState.action === 'relevant' ? 'relevante' : 'irrelevante'
    
    await handleAction(modalState.publicationId, () => {
      if (modalState.action === 'relevant') {
        return PublicationService.markAsRelevant(modalState.publicationId!, reason)
      } else {
        return PublicationService.markAsIrrelevant(modalState.publicationId!, reason)
      }
    }, `Publicação marcada como ${actionText}`)

    setModalState({ isOpen: false, action: null, publicationId: null })
  }

  const handleModalClose = () => {
    setModalState({ isOpen: false, action: null, publicationId: null })
  }

  const toggleNotes = (publicationId: number) => {
    setShowNotes(prev => ({
      ...prev,
      [publicationId]: !prev[publicationId]
    }))
  }

  const handleSetPriority = (publication: DJENPublication) => {
    setPriorityModalState({
      isOpen: true,
      publication
    })
  }

  const handlePriorityChange = async (priority: PublicationPriority, _reason?: string) => {
    if (!priorityModalState.publication) return

    const priorityLabels = {
      'LOW': 'Baixa',
      'MEDIUM': 'Média', 
      'HIGH': 'Alta',
      'URGENT': 'Urgente'
    }

    await handleAction(
      priorityModalState.publication.id, 
      () => PublicationService.setPriority(priorityModalState.publication!.id, priority),
      `Prioridade alterada para ${priorityLabels[priority]}`
    )

    setPriorityModalState({ isOpen: false, publication: null })
  }

  const handlePriorityModalClose = () => {
    setPriorityModalState({ isOpen: false, publication: null })
  }

  const getStatusBadge = (status: string) => {
    const color = PublicationService.getStatusColor(status)
    const label = PublicationService.getStatusLabel(status)
    return <Badge className={color}>{label}</Badge>
  }

  const getPriorityBadge = (priority: string, publication: DJENPublication) => {
    const color = PublicationService.getPriorityColor(priority)
    const label = PublicationService.getPriorityLabel(priority)
    return (
      <button
        type="button"
        onClick={() => handleSetPriority(publication)}
        className={cn(
          'inline-flex items-center rounded-4xl border border-transparent px-2 py-0.5 text-xs font-medium transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
          color,
        )}
        title="Clique para alterar prioridade"
      >
        {label}
      </button>
    )
  }

  if (publications.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
          <FileText className="size-8 text-muted-foreground" aria-hidden="true" />
          <h3 className="font-medium">Nenhuma publicação encontrada</h3>
          <p className="text-sm text-muted-foreground">Não há publicações que correspondam aos filtros aplicados.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="min-w-0 overflow-x-auto">
      <Table className="w-full min-w-[20rem] table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="whitespace-normal">Publicação</TableHead>
            <TableHead className="w-28 whitespace-normal sm:w-40">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {publications.map((publication) => (
            <Fragment key={publication.id}>
              <TableRow
                className="cursor-pointer"
                onClick={() => onView(publication)}
              >
                <TableCell className="whitespace-normal align-top">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                    {/* Coluna principal - Texto da publicação */}
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        {publication.isLocked && (
                          <Lock className="size-4 text-destructive" aria-label="Publicação bloqueada" />
                        )}
                        <p className="break-words text-sm font-medium text-foreground">
                          {publication.tipoComunicacao}
                        </p>
                        {getStatusBadge(publication.analysisStatus)}

                        {/* Auto-Relevance Score Badge */}
                        {publication.autoRelevanceScore !== undefined && publication.autoRelevanceScore !== null && (
                          <Badge
                            className={relevanceScoreClass(publication.autoRelevanceScore)}
                            title={`Score automático de relevância: ${publication.autoRelevanceScore}/100${publication.autoDecided ? ' (Auto-classificada)' : ''}`}
                          >
                            <Zap aria-hidden="true" />
                            {publication.autoRelevanceScore}
                          </Badge>
                        )}

                        {/* Badge de Vinculação ao Processo */}
                        {publication.linkedCaseId && (
                          <Badge
                            className="bg-primary/10 text-primary"
                            title={`Vinculado ao processo ${publication.caseInternalNumber || ''}`}
                          >
                            <LinkIcon aria-hidden="true" />
                            {publication.caseInternalNumber || 'Vinculado'}
                          </Badge>
                        )}

                        {/* Badge de Alerta de Prazo (Fase 3) - Intimações sem tarefa */}
                        {publication.tipoComunicacao === 'Intimação' &&
                         !publication.taskCreated &&
                         publication.analysisStatus !== 'TASK_CREATED' &&
                         publication.analysisStatus !== 'ARCHIVED' && (
                          <Badge
                            variant="destructive"
                            className="font-semibold"
                            title="Intimação sem tarefa criada - Risco de perda de prazo!"
                          >
                            <AlertTriangle aria-hidden="true" />
                            Sem tarefa
                          </Badge>
                        )}
                      </div>
                      <p className="mb-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        <span>Processo:</span>
                        {publication.linkedCaseId ? (
                          <Link
                            to={`/cases/${publication.linkedCaseId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="break-all font-medium text-primary hover:underline"
                            title={`Ver processo ${publication.caseInternalNumber || ''}`}
                          >
                            {publication.numeroProcessoComMascara}
                          </Link>
                        ) : (
                          <span className="break-all">{publication.numeroProcessoComMascara}</span>
                        )}
                        <CopyableProcessNumber
                          number={publication.numeroProcessoComMascara}
                          variant="badge"
                          className="text-xs"
                        />
                      </p>
                      <p className="break-words text-sm leading-relaxed text-foreground/80">
                        {PublicationService.truncateText(publication.texto, 200)}
                      </p>
                    </div>
                    
                    {/* Coluna lateral - Tribunal e metadados */}
                    <div className="min-w-0 sm:w-48 sm:shrink-0 sm:text-right">
                      <div className="text-sm">
                        <p className="font-medium text-foreground">{publication.siglaTribunal}</p>
                        <p className="mb-2 break-words text-xs text-muted-foreground">{publication.nomeOrgao}</p>
                        <p className="mb-2 text-xs text-muted-foreground">
                          {PublicationService.formatDate(publication.dataDisponibilizacao)}
                        </p>
                        
                        {/* Informações do advogado principal */}
                        {publication.advogadoPrincipal && (
                          <div className="mb-2 rounded-md border border-l-4 border-border border-l-primary bg-muted/40 p-2 text-left">
                            <p className="mb-1 text-xs font-medium text-foreground">
                              Advogado Responsável
                            </p>
                            <p className="break-words text-xs text-foreground">
                              {publication.advogadoPrincipal.nome}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              OAB {publication.advogadoPrincipal.numeroOab}/{publication.advogadoPrincipal.ufOab}
                            </p>
                          </div>
                        )}
                        
                        {getPriorityBadge(publication.priority, publication)}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="align-top" onClick={(e) => e.stopPropagation()}>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Botões de Classificação Manual - Override da auto-relevância */}
                    {publication.analysisStatus === 'PENDING' && (
                      <>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon-sm"
                              onClick={() => handleMarkRelevant(publication)}
                              disabled={loading[publication.id]}
                              aria-label="Marcar como relevante"
                              className="text-primary"
                            >
                              <Check className="size-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Marcar como relevante (override manual)</TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon-sm"
                              onClick={() => handleMarkIrrelevant(publication)}
                              disabled={loading[publication.id]}
                              aria-label="Marcar como irrelevante"
                              className="text-destructive"
                            >
                              <X className="size-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Marcar como irrelevante (override manual)</TooltipContent>
                        </Tooltip>
                      </>
                    )}

                    {/* Botão de Notas - Sempre disponível */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon-sm"
                          onClick={() => toggleNotes(publication.id)}
                          aria-label="Notas"
                          aria-pressed={!!showNotes[publication.id]}
                          className={cn(showNotes[publication.id] && 'bg-accent text-accent-foreground')}
                        >
                          <MessageSquare className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Ver/adicionar notas</TooltipContent>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
              
              {showNotes[publication.id] && (
                <TableRow key={`notes-${publication.id}`}>
                  <TableCell colSpan={2} className="whitespace-normal bg-muted/40">
                    <PublicationNotes
                      publicationId={publication.id}
                      className="border-0 bg-transparent shadow-none"
                    />
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          ))}
        </TableBody>
      </Table>

      <PublicationActionModal
        isOpen={modalState.isOpen}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
        title={modalState.action === 'relevant' ? 'Marcar como Relevante' : 'Marcar como Irrelevante'}
        action={modalState.action || 'relevant'}
        loading={modalState.publicationId ? loading[modalState.publicationId] : false}
      />

      <SetPriorityModal
        isOpen={priorityModalState.isOpen}
        onClose={handlePriorityModalClose}
        onConfirm={handlePriorityChange}
        publication={priorityModalState.publication}
        loading={priorityModalState.publication ? loading[priorityModalState.publication.id] : false}
      />
    </div>
  )
}